import { useCallback, useEffect, useState } from "react";
import type { UseKarlFocusRelease } from "./types";

/**
 * Lets the estimator take the plan back while Karl is standing on one run.
 *
 * > *"show all does nothing."*
 *
 * It did nothing because Karl's focus is **derived**: `CablesChatPanel` reads
 * `ask.focus.circuitId` off the open question and pushes it up in an effect, so
 * there is no state to clear. "Show all" cleared the estimator's own selection,
 * the focus went on holding the isolation, and the one way back was dead —
 * which is most of the session, because the walk starts on its own.
 *
 * So this sits BESIDE the focus rather than fighting it. Writing null into
 * Karl's own state would have been overwritten the moment anything re-derived
 * it, and would have made his card disagree with the run it is asking about.
 *
 * **It lasts exactly one question.** Karl frames the next ask himself — the
 * estimator did not choose that run and may be looking anywhere on the sheet,
 * which is the whole point of the walk — so a release that outlived the ask
 * would quietly disable his framing for the rest of the session.
 */
export const useKarlFocusRelease: UseKarlFocusRelease = ({
  karlRouteUnderReview,
}) => {
  const [isReleased, setReleased] = useState(false);

  const releaseKarlFocus = useCallback(() => setReleased(true), []);

  // Keyed on the focus itself, so it re-arms when Karl MOVES and not when he
  // merely re-renders. Answering an ask about the same run twice is not a move.
  useEffect(() => setReleased(false), [karlRouteUnderReview]);

  return {
    routeUnderReviewOnPlan: isReleased ? null : karlRouteUnderReview,
    releaseKarlFocus,
  };
};
